export type GlossaryRegime = "UK" | "US" | "Both";

export interface GlossaryEntry {
  term: string;
  regime: GlossaryRegime;
  category: string;
  short: string;
  detail: string;
  seeAlso?: string[];
  usAnalog?: string;
}

export const GLOSSARY: GlossaryEntry[] = [
  // ----- Contract forms -----
  {
    term: "JCT",
    regime: "UK",
    category: "Contract forms",
    short: "Joint Contracts Tribunal — the standard UK building-contract suite.",
    detail:
      "JCT 2024 (and prior 2016 / 2011 editions) — Standard Building Contract, Design & Build, Intermediate, Minor Works. Traditional employer-led model, monthly interim certificates, retention 3–5%, retention released half at practical completion and balance at end of defects period. Variation + claim mechanics are retrospective — the QS values them at month-end.",
    usAnalog: "AIA A101 / A102 / A201 family — closest analog.",
    seeAlso: ["NEC", "Final account"],
  },
  {
    term: "NEC",
    regime: "UK",
    category: "Contract forms",
    short: "Engineering and Construction Contract — collaborative, programme-driven UK form.",
    detail:
      "NEC4 (and prior NEC3) ECC. Common on UK infrastructure and public-sector projects. Two key mechanics differ from JCT: (1) the programme is contractual — late updates trigger compensation events; (2) every change is logged as a compensation event with a price + time impact agreed week-by-week, instead of waiting for end-of-job claims. Cost-value reconciliation under NEC is therefore less retrospective than under JCT.",
    usAnalog: "ConsensusDocs collaborative / IPD agreements — partial analog.",
    seeAlso: ["JCT", "Final account"],
  },

  // ----- Payment regime -----
  {
    term: "Pay-less notice",
    regime: "UK",
    category: "Payment regime",
    short:
      "Statutory notice required under the UK Construction Act before a payer can deduct from a sum already notified as due.",
    detail:
      "Construction Act s.111. If a payer wants to pay less than the sum stated in the s.110A payment notice (or the payee's default notice), the payer must serve a pay-less notice before the final date for payment, stating the new sum and the basis for the reduction. Without it, the full notified sum becomes payable. Abuse pattern: large unjustified deductions filed at the deadline, betting that the payee won't adjudicate.",
    usAnalog:
      "Notice of withholding / set-off — but the US has no statutory deadline regime; behaviour is contract-specific.",
    seeAlso: ["Adjudication", "Final account"],
  },
  {
    term: "Adjudication",
    regime: "UK",
    category: "Payment regime",
    short: "28-day binding-pending-final-resolution dispute mechanism in UK construction.",
    detail:
      "Construction Act s.108. A statutory right available at any point in the contract — refer the dispute, an adjudicator decides within 28 days, decision is binding pending final litigation or arbitration. Most adjudication decisions are not subsequently litigated; in practice, the adjudicator's call settles the matter. Significantly faster and cheaper than litigation.",
    usAnalog:
      "No close US analog — closer to mediation in speed but binding like arbitration. Some states have prompt-payment-act fast tracks but none match the breadth.",
    seeAlso: ["Pay-less notice"],
  },

  // ----- Project closeout -----
  {
    term: "Final account",
    regime: "UK",
    category: "Project closeout",
    short: "The binding settlement of contract value at project end.",
    detail:
      "Triggered at substantial / practical completion + end of defects period. The contractor's QS spends 2–6 months finalising variation values, outstanding claims (loss & expense, prolongation, disruption), daywork sheets, PC and provisional sum reconciliations, and retention release. Once the final certificate is issued and unchallenged for the contractual period, the matter is conclusive — a project's reported profit doesn't truly crystallise until then.",
    usAnalog: "Final pay-application + close-out audit. The US has no equivalent of the conclusive-evidence final certificate.",
    seeAlso: ["JCT", "NEC", "Retention"],
  },

  // ----- Materials & retention -----
  {
    term: "Vesting",
    regime: "UK",
    category: "Materials & retention",
    short: "Legal title transfer for materials before installation, evidenced by a vesting certificate.",
    detail:
      "When the contractor claims 90% on materials on site (or typically 75% off-site), title must transfer from supplier → contractor → employer. The vesting certificate proves materials are identified, title has passed (not subject to retention-of-title to upstream supplier), insured, and stored properly. Common audit failure: claim materials value while supplier still has retention-of-title because the supplier's invoice hasn't been paid — if supplier goes insolvent, the materials can be reclaimed.",
    usAnalog:
      "No exact analog. Closest: payment for stored materials with title-transfer language in the AIA G702/G703.",
    seeAlso: ["Retention-of-title", "CIS"],
  },
  {
    term: "Retention-of-title",
    regime: "Both",
    category: "Materials & retention",
    short: "Supplier clause keeping ownership of materials until invoice is paid in full.",
    detail:
      "Standard supplier T&Cs (Romalpa clause in UK; conditional sale / PMSI in US). If the buyer insolvents or fails to pay, the supplier can reclaim the materials. Combined with construction-tier insolvency, can mean a contractor pays its sub for materials, the sub never pays the supplier, the supplier reclaims the materials, and the contractor is out of pocket. Vesting certificate plus proof-of-payment chain is the defence.",
    usAnalog:
      "Same concept; UCC Article 9 governs perfection of security interests. Construction-specific liens (mechanic's lien) layer on top.",
    seeAlso: ["Vesting", "CIS"],
  },

  // ----- Tax & regulatory -----
  {
    term: "CIS",
    regime: "UK",
    category: "Tax & regulatory",
    short:
      "Construction Industry Scheme — HMRC withholding regime on payments from main contractors to subcontractors.",
    detail:
      "Two outcomes: gross-payment-status (sub paid in full, must meet HMRC turnover + compliance + business test) or net (main withholds 20% if verified, 30% if unverified, remitted to HMRC). HMRC withdraws gross-payment-status when a sub falls behind on tax filings — a direct distress signal. Worth surfacing as a sub-tier-risk feature in any UK-relevant model.",
    usAnalog:
      "No direct analog. Closest: 1099-NEC / backup-withholding for subs without valid TIN — much narrower in scope.",
    seeAlso: ["Vesting"],
  },
];

export function groupByCategory(): Map<string, GlossaryEntry[]> {
  const map = new Map<string, GlossaryEntry[]>();
  for (const e of GLOSSARY) {
    const list = map.get(e.category) ?? [];
    list.push(e);
    map.set(e.category, list);
  }
  return map;
}
